export type CartSortMode = 'original' | 'organized';

export interface CartPresentationLine {
  readonly lineId: string;
  readonly originalOrder: number;
  readonly organizedOrder: number;
}

export interface CatalogPresentationProduct {
  readonly productId: string;
  readonly categoryId: string;
}

export type LinkupSlot = 'main' | 'snack' | 'drink';

export interface LinkupCandidate {
  readonly lineId: string;
  readonly eligibleSlots: readonly LinkupSlot[];
}

export interface ReviewPresentationItem {
  readonly reviewId: string;
  readonly categoryId: string;
  readonly priority: 'high' | 'normal';
  readonly dayBucket: string;
  readonly searchText: string;
}

export interface ReviewFilter {
  readonly categoryId: string;
  readonly tab: 'all' | 'high' | 'today';
  readonly query: string;
}

export function sortCartLines<T extends CartPresentationLine>(
  items: readonly T[],
  mode: CartSortMode,
): T[] {
  const key: keyof Pick<CartPresentationLine, 'originalOrder' | 'organizedOrder'> =
    mode === 'original' ? 'originalOrder' : 'organizedOrder';
  return [...items].sort((a, b) => a[key] - b[key]);
}

export function filterCatalogProducts<T extends CatalogPresentationProduct>(
  items: readonly T[],
  categoryId: string,
): T[] {
  return categoryId === 'all' ? [...items] : items.filter((item) => item.categoryId === categoryId);
}

export function filterLinkupCandidates<T extends LinkupCandidate>(
  items: readonly T[],
  slot: LinkupSlot,
): T[] {
  return items.filter((item) => item.eligibleSlots.includes(slot));
}

export function filterReviewItems<T extends ReviewPresentationItem>(
  items: readonly T[],
  filter: ReviewFilter,
): T[] {
  const query = filter.query.trim().toLowerCase();
  return items.filter((item) => {
    if (filter.categoryId !== 'all' && item.categoryId !== filter.categoryId) return false;
    if (filter.tab === 'high' && item.priority !== 'high') return false;
    if (filter.tab === 'today' && item.dayBucket !== 'today') return false;
    if (query && !item.searchText.toLowerCase().includes(query)) return false;
    return true;
  });
}
